import type { IClaimAiNote } from '@/types/claim';
import type {
  IAdminUser,
  IAdminUserUpdate,
  IUsersListParams,
  IUsersListResponse,
} from '@/types/user';

import { apiClient } from '@/utils/apiClient';

export interface IAdminDashboardData {
  totalOutstandingBalance: number;
  totalUsers: number;
  topupsNext30Days: number;
  topupsNext90Days: number;
  currentMonth: {
    approvedClaimsCount: number;
    totalClaimAmount: number;
    topCategories: Array<{
      categoryId: number;
      categoryName: string;
      categoryEmoji: string;
      amount: number;
    }>;
  };
  previousMonth: {
    paidClaimsCount: number;
    totalClaimAmount: number;
    topCategories: Array<{
      categoryId: number;
      categoryName: string;
      categoryEmoji: string;
      amount: number;
    }>;
  };
}

export interface IAdminClaim {
  id: string;
  userEmail: string;
  userName: string;
  userType: string | null;
  amount: number;
  category: number;
  categoryName: string;
  details: string;
  receipt: string;
  status: string;
  flag: 'yellow' | 'red' | null;
  aiNotes: IClaimAiNote[];
  createdAt: string | null;
}

export interface IAdminClaimsParams {
  limit?: number;
  exclusiveStartKey?: string;
  status?: string;
  flag?: string;
  userType?: string;
  name?: string;
  startDate?: string;
  user?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface IAdminClaimsResponse {
  items: IAdminClaim[];
  nextCursor: string | null;
}

export const fetchAdminDashboardMetrics = async (): Promise<IAdminDashboardData> => {
  const { data } = await apiClient.get<IAdminDashboardData>('/admin/dashboard-metrics');
  return data;
};

export const fetchUsers = async (params: IUsersListParams): Promise<IUsersListResponse> => {
  const { data } = await apiClient.get<IUsersListResponse>('/admin/users', { params });
  return data;
};

export const fetchAdminUser = async (userId: string): Promise<IAdminUser> => {
  const { data } = await apiClient.get<IAdminUser>(`/admin/users/${encodeURIComponent(userId)}`);
  return data;
};

export const updateAdminUser = async (userId: string, body: IAdminUserUpdate): Promise<void> => {
  await apiClient.put(`/admin/users/${encodeURIComponent(userId)}`, body);
};

export const fetchAdminClaims = async (
  params: IAdminClaimsParams,
): Promise<IAdminClaimsResponse> => {
  const { data } = await apiClient.get<IAdminClaimsResponse>('/claims', {
    params: { ...params, all: 'true' },
  });
  return data;
};

export interface IUpdateClaimStatusParams {
  claimId: string;
  userEmail: string;
  status: string;
  flag?: 'yellow' | 'red' | null;
}

export const updateClaimStatus = async (params: IUpdateClaimStatusParams): Promise<void> => {
  const encodedClaimId = encodeURIComponent(params.claimId);
  await apiClient.put(`/admin/claims/${encodedClaimId}/status`, {
    status: params.status,
    userEmail: params.userEmail,
    ...(params.status === 'pending' ? { flag: params.flag ?? null } : {}),
  });
};

export interface IUpdateClaimCategoryParams {
  claimId: string;
  userEmail: string;
  category: number;
}

export const updateClaimCategory = async (params: IUpdateClaimCategoryParams): Promise<void> => {
  const encodedClaimId = encodeURIComponent(params.claimId);
  await apiClient.put(`/admin/claims/${encodedClaimId}/category`, {
    category: params.category,
    userEmail: params.userEmail,
  });
};

export interface IBulkApproveClaimItem {
  claimId: string;
  userEmail: string;
}

export interface IBulkApproveClaimsParams {
  claims: IBulkApproveClaimItem[];
  newStatus?: string;
}

export interface IBulkApproveResultItem {
  claimId: string;
  success: boolean;
  error?: string;
}

export interface IBulkApproveClaimsResponse {
  approvedBy: string;
  newStatus: string;
  results: IBulkApproveResultItem[];
}

export const bulkApproveClaims = async (
  params: IBulkApproveClaimsParams,
): Promise<IBulkApproveClaimsResponse> => {
  const { data } = await apiClient.post<IBulkApproveClaimsResponse>(
    '/admin/claims/bulk-approve',
    params,
  );
  return data;
};

export interface IExportClaimsParams {
  status?: string;
  flag?: string;
  userType?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
  user?: string;
}

export interface IExportClaimsResponse {
  items: IAdminClaim[];
  totalCount: number;
  truncated: boolean;
}

export const exportClaims = async (
  params: IExportClaimsParams,
  signal?: AbortSignal,
): Promise<IExportClaimsResponse> => {
  const { data } = await apiClient.get<IExportClaimsResponse>('/admin/claims/export', {
    params,
    signal,
  });
  return data;
};

export interface IReceiptDownloadUrlResponse {
  downloadUrl: string;
  expiresIn: number;
}

export const getReceiptDownloadUrl = async (s3Key: string): Promise<string> => {
  const encodedS3Key = encodeURIComponent(s3Key);
  const { data } = await apiClient.get<IReceiptDownloadUrlResponse>(
    `/admin/receipts/${encodedS3Key}`,
  );
  return data.downloadUrl;
};

export interface IUserTypeOption {
  value: string;
  label: string;
}

export const fetchUserTypes = async (): Promise<IUserTypeOption[]> => {
  const { data } = await apiClient.get<{ items: IUserTypeOption[] }>('/user-types');
  return data.items;
};
